const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type TurnstileApi = {
  render: (el: HTMLElement, options: Record<string, unknown>) => string;
  remove: (id: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

export const turnstileSitekey = (): string =>
  process.env.TURNSTILE_SITEKEY || '';

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    const { promise, resolve, reject } = Promise.withResolvers<void>();
    scriptPromise = promise;
    const script = document.createElement('script');
    script.src = SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Turnstile failed to load'));
    };
    document.head.appendChild(script);
  }
  return scriptPromise;
}

export function renderTurnstile(
  element: HTMLElement,
  onToken: (token: string) => void,
): Promise<() => void> {
  const sitekey = turnstileSitekey();
  if (!sitekey) {
    onToken('');
    return Promise.resolve(() => undefined);
  }

  return loadTurnstile().then(() => {
    const { promise, resolve, reject } = Promise.withResolvers<() => void>();
    if (!window.turnstile) {
      reject(new Error('Turnstile failed to load'));
      return promise;
    }
    const id = window.turnstile.render(element, {
      sitekey,
      callback: (token: string) => onToken(token),
      'expired-callback': () => onToken(''),
      'error-callback': () => onToken(''),
    });
    resolve(() => window.turnstile?.remove(id));
    return promise;
  });
}

/** Renders a managed challenge and resolves with a single-use token. */
export function executeTurnstile(): Promise<string> {
  const sitekey = turnstileSitekey();
  if (!sitekey) return Promise.resolve('');

  const element = document.createElement('div');
  element.className = 'turnstile turnstile--floating';
  document.body.appendChild(element);

  const { promise, resolve, reject } = Promise.withResolvers<string>();
  const timer = window.setTimeout(() => {
    element.remove();
    reject(new Error('Bot check timed out'));
  }, 30000);

  renderTurnstile(element, (token) => {
    window.clearTimeout(timer);
    element.remove();
    if (token) resolve(token);
    else reject(new Error('Bot check failed'));
  }).catch((error) => {
    window.clearTimeout(timer);
    element.remove();
    reject(error);
  });

  return promise;
}
